import { useEffect, useRef } from 'react';
import { LoadMapAndSpiderfier, configFullScreen } from '../../utils/LoadMaps';
import './markers.css';

function LeafletPackingOrders({
    orders,
    id,
    onOrderClick,
    onOrderSelect,
    selectedOrderKeys,
    getPackingStatusColor
}) {
    const mapRef = useRef(null);
    const markersRef = useRef({});
    const zoom = 13;
    const scriptMapAndDraw = LoadMapAndSpiderfier();
    const centerDefault = { lat: -34.6037, lng: -58.3816 }; // Buenos Aires

    useEffect(() => {
        if (scriptMapAndDraw === "ready") {
            initMap();
        }
        // eslint-disable-next-line
    }, [orders, scriptMapAndDraw]);

    useEffect(() => {
        // Update marker icons when selection changes
        if (mapRef.current) {
            updateMarkerIcons();
        }
        // eslint-disable-next-line
    }, [selectedOrderKeys]);

    const initMap = () => {
        var L = window.L;

        // Destroy previous map if exists
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
        }

        // Filter orders with valid coordinates
        const ordersWithCoords = orders.filter(order =>
            order.address?.dom_latitud && order.address?.dom_longitud
        );

        const centro = ordersWithCoords.length > 0
            ? [parseFloat(ordersWithCoords[0].address.dom_latitud), parseFloat(ordersWithCoords[0].address.dom_longitud)]
            : [centerDefault.lat, centerDefault.lng];

        // Create map - Deshabilitar zoom con doble-click
        mapRef.current = L.map(id, {
            ...configFullScreen,
            doubleClickZoom: false
        }).setView(centro, zoom);

        L.tileLayer('http://{s}.tile.osm.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(mapRef.current);

        addMarkers(ordersWithCoords);
    };

    const updateMarkerIcons = () => {
        Object.keys(markersRef.current).forEach(orderNumber => {
            const { marker, order } = markersRef.current[orderNumber];
            const isSelected = selectedOrderKeys.includes(order.id);
            const icon = createMarkerIcon(order, isSelected);
            marker.setIcon(icon);
        });
    };

    const createMarkerIcon = (order, isSelected = false) => {
        var L = window.L;
        const statusColor = getPackingStatusColor(order.status?.id);

        // Si está seleccionado, agregar un indicador visual
        const checkmarkHtml = isSelected ? `
            <div style="
                position: absolute;
                top: -24px;
                left: -10px;
                width: 20px;
                height: 20px;
                background-color: #52c41a;
                border: 2px solid white;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                z-index: 1000;
            ">
                <svg width="12" height="12" viewBox="0 0 14 14" style="fill: white;">
                    <path d="M5.5 11L1 6.5L2.41 5.09L5.5 8.17L11.59 2.08L13 3.5L5.5 11Z"/>
                </svg>
            </div>
        ` : '';

        // Borde negro exterior
        const outerCircleStyles = `
            width: 22px;
            height: 25px;
            border-radius: 50%;
            background-color: #000;
            position: absolute;
            top: -20px;
            left: -6px;
        `;

        // Estilos para el círculo principal (color del estado)
        const circleStyles = `
            border: 6px solid ${statusColor};
            background-color: white;
            position: relative;
            ${isSelected ? 'box-shadow: 0 0 12px rgba(82, 196, 26, 0.6);' : ''}
        `;

        // Triángulo con borde negro
        const triangleOuterStyles = `
            width: 0;
            height: 0;
            border-left: 17px solid #000;
            border-top: 10px solid transparent;
            border-bottom: 10px solid transparent;
            transform: rotate(90deg);
            position: absolute;
            margin-left: -2.8px;
            margin-top: -6px;
        `;

        // Triángulo interior (color del estado)
        const triangleStyles = `
            border-left: 15px solid ${statusColor};
            border-top: 9px solid transparent;
            border-bottom: 9px solid transparent;
        `;

        return L.divIcon({
            iconAnchor: [6, 13],
            labelAnchor: [-6, 0],
            popupAnchor: [0, -36],
            html: `
                ${checkmarkHtml}
                <div style="${outerCircleStyles}"></div>
                <div style="${circleStyles}" class='circle'></div>
                <div style="${triangleOuterStyles}"></div>
                <div style="${triangleStyles}" class='triangle'></div>
            `
        });
    };

    const addMarkers = (ordersWithCoords) => {
        var L = window.L;
        const mymap = mapRef.current;

        if (!mymap) return;

        // Clear previous markers
        mymap.eachLayer(function (layer) {
            if (!!layer.toGeoJSON) {
                mymap.removeLayer(layer);
            }
        });

        markersRef.current = {};

        var oms = new window.OverlappingMarkerSpiderfier(mymap, {
            keepSpiderfied: true,
            nearbyDistance: 20,
            spiralFootSeparation: 30,
            spiralLengthStart: 15,
            spiralLengthFactor: 5,
            circleFootSeparation: 25,
            circleStartAngle: 0
        });

        let centerMap = [];

        ordersWithCoords.forEach(order => {
            const lat = parseFloat(order.address.dom_latitud);
            const lng = parseFloat(order.address.dom_longitud);
            const markerCenter = [lat, lng];

            const isSelected = selectedOrderKeys.includes(order.id);
            const icon = createMarkerIcon(order, isSelected);

            const marker = L.marker(markerCenter, { icon: icon });

            // Popup content con botón para abrir modal
            const clientName = order.buyer ? `${order.buyer.nombre || ''} ${order.buyer.apellido || ''}`.trim() : 'N/A';
            const popupContent = `
                <div style="font-family: Arial, sans-serif; min-width: 250px;">
                    <div style="margin-bottom: 12px;">
                        <b style="font-size: 14px; color: #262626;">Orden #${order.id}</b>
                    </div>
                    <div style="margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #e8e8e8;">
                        <div style="margin-bottom: 4px;">
                            <span style="color: #8c8c8c; font-size: 12px;">Cliente:</span>
                            <span style="color: #262626; font-size: 12px; margin-left: 4px;">${clientName}</span>
                        </div>
                        <div style="margin-bottom: 4px;">
                            <span style="color: #8c8c8c; font-size: 12px;">Dirección:</span>
                            <span style="color: #262626; font-size: 12px; margin-left: 4px;">${order.address.dom_calle || ''} ${order.address.dom_altura || ''}</span>
                        </div>
                        <div>
                            <span style="color: #8c8c8c; font-size: 12px;">Estado:</span>
                            <span style="color: #262626; font-size: 12px; margin-left: 4px;">${order.actual_package_status || 'N/A'}</span>
                        </div>
                    </div>
                    <button
                        onclick="window.openOrderDetail_${order.id}()"
                        style="
                            width: 100%;
                            padding: 8px 16px;
                            background-color: #1890ff;
                            color: white;
                            border: none;
                            border-radius: 4px;
                            cursor: pointer;
                            font-size: 13px;
                            font-weight: 500;
                            transition: background-color 0.3s;
                        "
                        onmouseover="this.style.backgroundColor='#40a9ff'"
                        onmouseout="this.style.backgroundColor='#1890ff'"
                    >
                        Ver más detalles
                    </button>
                </div>
            `;

            // Crear función global para este marker específico
            window[`openOrderDetail_${order.id}`] = () => {
                onOrderClick(order);
            };

            marker.bindPopup(popupContent, {
                closeButton: true,
                autoClose: false,
                closeOnClick: false,
                maxWidth: 300
            });

            mymap.addLayer(marker);
            oms.addMarker(marker);

            // Doble-click - seleccionar/deseleccionar orden
            marker.on("dblclick", function (event) {
                L.DomEvent.stopPropagation(event);
                L.DomEvent.preventDefault(event);
                marker.closePopup();
                onOrderSelect(order);
            });

            // Store reference
            markersRef.current[order.id] = { marker, order };

            centerMap.push(markerCenter);
        });

        // Manejar eventos del spider
        oms.addListener('click', function(marker) {
            // Click simple - abrir popup
            marker.openPopup();
        });

        oms.addListener('spiderfy', function(markers) {
            // Cuando se abre el spider, cerrar todos los popups
            markers.forEach(function(marker) {
                marker.closePopup();
            });
        });

        if (centerMap.length > 0) {
            mymap.fitBounds(centerMap);
        }
    };

    return (
        <div id={id} style={{ width: "100%", height: "500px", borderRadius: '8px' }}></div>
    );
}

export default LeafletPackingOrders;
